import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useConnections } from '../../contexts/ConnectionsContext';
import { useApp } from '../../contexts/AppContext';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../../firebaseConfig';
import BeltBadge from '../common/BeltBadge';
import DojoMark from '../common/DojoMark';
import { useScore } from '../../hooks/useScore';

/**
 * The app shell.
 *
 * Rebuilt against the phone's ModernTabBar rather than restyled. Three things
 * came from there and matter:
 *
 *  - the active treatment is a pill: a soft red wash inside a 2px red border,
 *    with the icon tinted red. It was a monochrome inversion here -- a white
 *    block on dark, a black block on light -- which belongs to no palette and
 *    read as a different product to the screens it framed.
 *  - the bar carries a 2px top border, not a hairline, matching every other
 *    edge in the design.
 *  - five primary destinations, not sixteen. The phone's DEFAULT_MENU_KEYS is
 *    five plus a Personal tab; the web was putting all sixteen screens in a
 *    horizontally scrolling strip, so the nav was itself something you had to
 *    navigate. The rest now live behind More.
 *
 * The split is the only real design decision here: a sidebar has room to show
 * everything at once and a phone does not, so on desktop More is a labelled
 * second group that is always visible, and on mobile it is a sheet.
 */

type ScreenId =
  | 'dashboard' | 'tasks' | 'gantt' | 'calendar' | 'connections' | 'sensei'
  | 'habits' | 'lists' | 'mood' | 'affirmations' | 'notes' | 'budget'
  | 'support' | 'analytics' | 'admin' | 'connectors' | 'settings';

interface NavigationProps {
  currentScreen: ScreenId;
  onNavigate: (screen: ScreenId) => void;
}

const ADMIN_EMAILS = ['chris@splat.sh', 'christos@splat.sh'];

/** The pill's wash, from the phone's accentSoft. */
const ACTIVE_WASH_DARK = 'rgba(227,66,52,0.15)';
const ACTIVE_WASH_LIGHT = 'rgba(227,66,52,0.10)';
const PRIMARY = '#E34234';

const icon = (d: React.ReactNode) => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={2}
    viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
    {d}
  </svg>
);

interface Item { id: ScreenId; name: string; icon: React.ReactNode; }

/** What the phone puts in the tab bar, mapped onto the screens the web has. */
const PRIMARY_ITEMS: Item[] = [
  { id: 'dashboard', name: 'Dashboard', icon: icon(<><rect x="3" y="3" width="7" height="9" rx="1.5" /><rect x="14" y="3" width="7" height="5" rx="1.5" /><rect x="14" y="12" width="7" height="9" rx="1.5" /><rect x="3" y="16" width="7" height="5" rx="1.5" /></>) },
  { id: 'tasks', name: 'Tasks', icon: icon(<><path d="M9 11l3 3 8-8" /><path d="M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9" /></>) },
  { id: 'habits', name: 'Habits', icon: icon(<path d="M12 2s4 4 4 8a4 4 0 0 1-8 0c0-1.5.5-2.5 1-3.5M7 14a5 5 0 0 0 10 0c0 4-2 8-5 8s-5-4-5-8z" />) },
  { id: 'calendar', name: 'Calendar', icon: icon(<><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>) },
  { id: 'sensei', name: 'Sensei AI', icon: icon(<path d="M13 2L4 14h7v8l9-12h-7z" />) },
];

const SECONDARY_ITEMS: Item[] = [
  { id: 'gantt', name: 'Gantt', icon: icon(<path d="M4 6h10M4 12h16M4 18h7" />) },
  { id: 'lists', name: 'Lists', icon: icon(<path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />) },
  { id: 'connections', name: 'Connections', icon: icon(<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></>) },
  { id: 'notes', name: 'Notes', icon: icon(<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M8 13h8M8 17h5" /></>) },
  { id: 'mood', name: 'Feelings', icon: icon(<><circle cx="12" cy="12" r="10" /><path d="M8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01" /></>) },
  { id: 'affirmations', name: 'Affirmations', icon: icon(<path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1L12 21l7.7-7.6 1.1-1a5.5 5.5 0 0 0 0-7.8z" />) },
  { id: 'budget', name: 'Budget', icon: icon(<><path d="M12 1v22" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></>) },
  { id: 'analytics', name: 'Analytics', icon: icon(<path d="M3 3v18h18M7 15l4-4 3 3 5-6" />) },
  { id: 'support', name: 'Support', icon: icon(<path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 8.5 8.5 0 0 1-3.8-.9L3 21l1.9-5.2A8.4 8.4 0 0 1 12 3a8.4 8.4 0 0 1 9 8.5z" />) },
];

const SETTINGS_ITEM: Item = {
  id: 'settings', name: 'Settings',
  icon: icon(<><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-2.9 1.2V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 7 19.4a1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.7 1.7 0 0 0 3 15a1.7 1.7 0 0 0-1.6-1H1a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 3 8.6a1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1A1.7 1.7 0 0 0 9 3V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 2.9 1.2l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0 1.2 2.9H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" /></>),
};

const ADMIN_ITEM: Item = {
  id: 'admin', name: 'Admin',
  icon: icon(<><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="M9 12l2 2 4-4" /></>),
};

const Navigation: React.FC<NavigationProps> = ({ currentScreen, onNavigate }) => {
  const { score } = useScore();
  const { isDark, colors } = useTheme();
  const { hasNewRequests } = useConnections();
  const { state } = useApp();
  const isAdmin = ADMIN_EMAILS.includes(state.user?.email || '');
  const [supportUnread, setSupportUnread] = useState(0);
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const chatRef = doc(db, 'supportChats', `support_${uid}`);
    const unsubscribe = onSnapshot(chatRef, (snap) => {
      if (snap.exists()) setSupportUnread(snap.data().unreadByUser || 0);
    }, () => { /* doc may not exist yet */ });
    return () => unsubscribe();
  }, []);

  const wash = isDark ? ACTIVE_WASH_DARK : ACTIVE_WASH_LIGHT;

  const dotFor = (id: ScreenId) =>
    (id === 'connections' && hasNewRequests) || (id === 'support' && supportUnread > 0);

  const go = (id: ScreenId) => { onNavigate(id); setMoreOpen(false); };

  /** A sidebar row. */
  const SideRow: React.FC<{ item: Item }> = ({ item }) => {
    const active = currentScreen === item.id;
    return (
      <button
        type="button"
        onClick={() => go(item.id)}
        aria-current={active ? 'page' : undefined}
        className={`group flex items-center gap-3 px-3 py-[10px] w-full rounded-[12px] border-2
          text-[14px] font-extrabold transition-colors
          ${active
            ? 'border-dojo-red text-dojo-red'
            : `border-transparent ${colors.textSecondary} ${isDark ? 'hover:bg-dojo-surfaceDeep hover:text-slate-200' : 'hover:bg-stone-100 hover:text-stone-900'}`}`}
        style={active ? { backgroundColor: wash } : undefined}
      >
        <span className="relative flex-shrink-0">
          {item.icon}
          {dotFor(item.id) && (
            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-dojo-red
              ring-2" style={{ ['--tw-ring-color' as any]: isDark ? '#1B2A32' : '#FFFFFF' }} />
          )}
        </span>
        {item.name}
      </button>
    );
  };

  return (
    <>
      {/* ------------------------------- Desktop ------------------------------- */}
      <div className={`hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0 lg:border-r-2
        ${colors.sidebarBg} ${colors.cardBorder}`}>
        <div className="flex flex-col flex-grow pt-6 pb-4 overflow-y-auto">
          <div className="flex items-center gap-3 flex-shrink-0 px-5 mb-6">
            {/* Drawn, so the wordmark and the tab icon are the same mark. */}
            <DojoMark
              size={38}
              title="taskDojo"
              outlineColor={isDark ? 'rgba(250,247,240,0.22)' : undefined}
            />
            <span className={`text-[19px] font-black tracking-[-0.5px] ${colors.text}`}>taskDojo</span>
          </div>

          {/* Rank. Sits under the wordmark rather than beside it: a sidebar has
              the width for the belt name and point count, where the phone's
              header only has room for the ring. */}
          <div className={`mx-4 mb-5 px-3 py-3 rounded-[14px] border-2 ${colors.cardBorder}
            ${isDark ? 'bg-dojo-surfaceDeep' : 'bg-stone-50'}`}>
            <BeltBadge score={score} size={38} showLabel />
          </div>

          <nav className="flex-1 px-4 space-y-1">
            {PRIMARY_ITEMS.map((item) => <SideRow key={item.id} item={item} />)}

            <p className={`px-3 pt-5 pb-2 text-[11px] font-bold uppercase tracking-[0.6px]
              ${colors.textMuted}`}>
              More
            </p>
            {SECONDARY_ITEMS.map((item) => <SideRow key={item.id} item={item} />)}
          </nav>

          <div className={`px-4 pt-3 mt-2 border-t-2 ${colors.cardBorder} space-y-1`}>
            {isAdmin && <SideRow item={ADMIN_ITEM} />}
            <SideRow item={SETTINGS_ITEM} />
          </div>
        </div>
      </div>

      {/* -------------------------------- Mobile -------------------------------- */}
      <nav className={`lg:hidden fixed bottom-0 left-0 right-0 z-50 border-t-2
        ${colors.card} ${colors.cardBorder}`}>
        <div className="flex pt-[10px] pb-1 px-2">
          {PRIMARY_ITEMS.slice(0, 4).map((item) => {
            const active = currentScreen === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => go(item.id)}
                aria-current={active ? 'page' : undefined}
                className="flex-1 py-1 flex flex-col items-center gap-1"
              >
                <span
                  className={`relative w-[54px] h-[38px] rounded-[19px] border-2 flex items-center justify-center
                    transition-colors ${active ? 'border-dojo-red' : 'border-transparent'}`}
                  style={active ? { backgroundColor: wash } : undefined}
                >
                  <span className={active ? 'text-dojo-red' : colors.textSecondary}>{item.icon}</span>
                  {dotFor(item.id) && (
                    <span className="absolute -top-px right-[26%] w-[11px] h-[11px] rounded-full bg-dojo-red" />
                  )}
                </span>
                <span className={`text-[11px] font-extrabold ${active ? 'text-dojo-red' : colors.textSecondary}`}>
                  {item.name}
                </span>
              </button>
            );
          })}

          {/* More. The phone's fifth slot is its Personal tab; here it opens the
              rest of the app, which is what a phone-width screen can afford. */}
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            aria-expanded={moreOpen}
            className="flex-1 py-1 flex flex-col items-center gap-1"
          >
            <span
              className={`relative w-[54px] h-[38px] rounded-[19px] border-2 flex items-center justify-center
                transition-colors ${moreOpen ? 'border-dojo-red' : 'border-transparent'}`}
              style={moreOpen ? { backgroundColor: wash } : undefined}
            >
              <span className={moreOpen ? 'text-dojo-red' : colors.textSecondary}>
                {icon(<><circle cx="5" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="12" r="1.6" /></>)}
              </span>
              {(hasNewRequests || supportUnread > 0) && (
                <span className="absolute -top-px right-[26%] w-[11px] h-[11px] rounded-full bg-dojo-red" />
              )}
            </span>
            <span className={`text-[11px] font-extrabold ${moreOpen ? 'text-dojo-red' : colors.textSecondary}`}>
              More
            </span>
          </button>
        </div>
      </nav>

      {/* The More sheet. */}
      {moreOpen && (
        <div
          className="lg:hidden fixed inset-0 z-[60] bg-black/60 flex items-end"
          onClick={() => setMoreOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={`w-full max-h-[80vh] overflow-y-auto rounded-t-[22px] border-t-2
              ${colors.card} ${colors.cardBorder} p-5 pb-8`}
          >
            <div className="flex items-center mb-4">
              <h2 className={`text-[22px] font-black tracking-[-0.6px] flex-1 ${colors.text}`}>More</h2>
              <button
                type="button"
                onClick={() => setMoreOpen(false)}
                aria-label="Close"
                className={`w-9 h-9 rounded-xl flex items-center justify-center ${colors.textSecondary}`}
              >
                {icon(<path d="M18 6L6 18M6 6l12 12" />)}
              </button>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {[
                PRIMARY_ITEMS[4],
                ...SECONDARY_ITEMS,
                ...(isAdmin ? [ADMIN_ITEM] : []),
                SETTINGS_ITEM,
              ].map((item) => {
                const active = currentScreen === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => go(item.id)}
                    className={`rounded-[14px] border-2 px-2 py-3 flex flex-col items-center gap-1.5
                      ${active
                        ? 'border-dojo-red text-dojo-red'
                        : `${colors.cardBorder} ${colors.textSecondary} ${isDark ? 'bg-dojo-surfaceDeep' : 'bg-stone-50'}`}`}
                    style={active ? { backgroundColor: wash } : undefined}
                  >
                    <span className="relative">
                      {item.icon}
                      {dotFor(item.id) && (
                        <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-dojo-red" />
                      )}
                    </span>
                    <span className="text-[11.5px] font-extrabold text-center leading-tight">
                      {item.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navigation;
